import { GameDateSchema } from "@pax/world";
import { z } from "zod";
import {
  CatalogFileName,
  CatalogSlug,
  ImageSource,
  PaginationQuery,
  PresetId,
  PresetVersionId,
  ProfileId,
  Sha256,
  Timestamp,
  type VideoSet,
  videoFileFields,
} from "../../common";
import { CreatedFrom } from "../../common/created-from";
import { ItemModeration } from "../../moderation/v1alpha1/schemas";
import { PresetCategory, PresetTag } from "./categories";

/** How long a version is its authors' alone before anyone may copy it, unless they allow it sooner. */
export const COPY_PROTECTION_DAYS = 30;

/** The most characters a version's label runs to. */
export const VERSION_LABEL_LENGTH = 20;

/** What a version's authors call it, such as 1.2 or Winter. A version needn't have one. */
export const VersionLabel = z
  .string()
  .trim()
  .min(1, { error: "Enter a label", abort: true })
  .max(VERSION_LABEL_LENGTH, { error: `${VERSION_LABEL_LENGTH} characters max` })
  .meta({
    id: "VersionLabel",
    description: "What its authors call the version, such as 1.2 or Winter.",
    example: "1.2",
  });
export type VersionLabel = z.infer<typeof VersionLabel>;

export const ArtworkTint = z
  .strictObject({
    hue: z.number().min(0).lt(360).meta({ description: "Degrees around the OKLCH wheel." }),
    chroma: z
      .number()
      .min(0)
      .max(1)
      .meta({ description: "How colourful the artwork is. A monochrome one is 0." }),
  })
  .meta({
    id: "ArtworkTint",
    description:
      "The colour artwork is mostly made of. How light the result should be is left to the reader's theme, so only the hue and its strength are carried.",
  });
export type ArtworkTint = z.infer<typeof ArtworkTint>;

export const TimedArtworkTint = ArtworkTint.extend({
  atMs: z.number().int().nonnegative(),
}).meta({
  id: "TimedArtworkTint",
  description: "One tint beginning at a presented-media timestamp.",
});
export type TimedArtworkTint = z.infer<typeof TimedArtworkTint>;

const TintTrack = z
  .array(TimedArtworkTint)
  .min(1)
  .refine(
    track =>
      track[0]?.atMs === 0 &&
      track.every((frame, index) => {
        const previous = track[index - 1];
        return index === 0 || (previous !== undefined && frame.atMs > previous.atMs);
      }),
    { message: "tint track must start at zero and increase strictly" },
  );

const LandingDurationMs = z.number().int().positive().meta({
  description: "How long one loop of the video lasts, in milliseconds.",
  example: 10_000,
});

export const LandingVideoSource = z
  .strictObject({
    url: z.url().meta({ description: "Where the MP4 is served." }),
    ...videoFileFields,
  })
  .meta({ id: "LandingVideoSource", description: "One size of the landing video." });
export type LandingVideoSource = z.infer<typeof LandingVideoSource>;

export const LandingVideo = z
  .strictObject({
    sources: z.array(LandingVideoSource).min(1).meta({
      description:
        "The video at half and at full size, smallest first. Play the smallest that covers its space.",
    }),
    durationMs: LandingDurationMs,
  })
  .meta({
    id: "LandingVideo",
    description:
      "The landing artwork in motion: a silent AV1 MP4 that loops. The web player uses the browser's decoder when available, and on the preset's own page a software decoder otherwise.",
  });
export type LandingVideo = z.infer<typeof LandingVideo>;

/** A landing video as the sources a player chooses from. */
export function landingVideoSet(video: LandingVideo): VideoSet {
  return {
    sources: video.sources.map(({ bytes: _, ...source }) => source),
    durationSeconds: video.durationMs / 1000,
    hasAudio: false,
  };
}

export const ArtworkSet = z
  .strictObject({
    sources: z.array(ImageSource).min(1).meta({
      description: "Every size of the artwork as AVIF, smallest first.",
    }),
    thumbhash: z.base64().meta({
      description: "The artwork a few pixels wide, to draw until a source arrives.",
    }),
  })
  .meta({ id: "ArtworkSet", description: "Preset artwork in several sizes." });
export type ArtworkSet = z.infer<typeof ArtworkSet>;

export const LandingMedia = z
  .strictObject({
    poster: ArtworkSet.meta({
      description: "The landing still, or the frame shown until its video can play.",
    }),
    video: LandingVideo.nullable(),
    tintTrack: TintTrack,
  })
  .meta({
    id: "LandingMedia",
    description:
      "Optional hero media and its publication-time colour track. A still has one tint at zero; a video carries timed tints.",
  });
export type LandingMedia = z.infer<typeof LandingMedia>;

export const PresetAuthor = z
  .object({
    profileId: ProfileId,
    username: z.string().nullable().meta({
      description: "The author's handle, or null while they have not claimed one.",
    }),
    displayName: z.string().min(1),
  })
  .meta({
    id: "PresetAuthor",
    description: "Who published the scenario, as their profile reads right now.",
  });
export type PresetAuthor = z.infer<typeof PresetAuthor>;

/**
 * What every view of a scenario shows. A listing and a detail are both this
 * plus the one thing they are for, so a page opened from a rail can be drawn
 * from what the rail already had.
 */
export const PresetStorefront = z.object({
  id: PresetId,
  slug: CatalogSlug,
  title: z.string().min(1),
  description: z.string(),
  author: PresetAuthor,
  cover: ArtworkSet.nullable(),
  landing: LandingMedia.nullable(),
  coverTint: ArtworkTint.nullable(),
  mapPreview: ArtworkSet.nullable().meta({
    description:
      "A still of the opening map, taken when the version was published. Shown in place of the live map, which is megabytes of geometry, until the reader asks for it.",
  }),
  regionCount: z.number().int().nonnegative(),
  entityCount: z.number().int().nonnegative(),
  startDate: GameDateSchema,
  category: PresetCategory,
  tag: PresetTag,
  publishedAt: Timestamp.meta({ description: "When the version shown was published." }),
  copyableFrom: Timestamp.meta({
    description: `When anyone may copy the version shown: as it was published, if its authors allow early copies, or ${COPY_PROTECTION_DAYS} days after.`,
  }),
  gamesStarted: z.number().int().nonnegative().meta({
    description: "Games started from any of its versions, not counting evaluations.",
  }),
  roundsPlayed: z.number().int().nonnegative().nullable().meta({
    description:
      "Rounds played in those games, as game events count them: close, not exact. Null when they couldn't be counted just now.",
  }),
});
export type PresetStorefront = z.infer<typeof PresetStorefront>;

export const PresetListing = PresetStorefront.extend({
  liveVersionId: PresetVersionId.meta({ description: "The live version the listing shows." }),
}).meta({ id: "PresetListing", description: "Storefront metadata for one published scenario." });
export type PresetListing = z.infer<typeof PresetListing>;

export const WorldDownload = z
  .object({
    url: z.string().startsWith("/"),
    digest: Sha256,
    bytes: z.number().int().positive(),
    throughRevision: z.number().int().nonnegative(),
  })
  .meta({
    id: "WorldDownload",
    description: "The immutable world artifact for this preset version.",
  });
export type WorldDownload = z.infer<typeof WorldDownload>;

export const PresetDetail = PresetStorefront.extend({
  presetVersionId: PresetVersionId,
  version: z.number().int().positive().meta({
    description: "Which version it is, counting each one published, from 1.",
  }),
  versionLabel: VersionLabel.nullable().meta({
    description: "What its authors call this version, such as 1.2, or null.",
  }),
  versionName: z.string().meta({
    description: "What its authors called this version, or empty when they didn't name it.",
  }),
  changeLog: z.string().meta({ description: "What its authors said changed in this version." }),
  createdFrom: CreatedFrom,
  world: WorldDownload,
}).meta({
  id: "PresetDetail",
  description: "Storefront metadata plus one immutable world artifact.",
});
export type PresetDetail = z.infer<typeof PresetDetail>;

export const PresetSlugParams = z
  .strictObject({ slug: CatalogSlug })
  .meta({ id: "PresetSlugParams", description: "A route parameter naming one preset." });
export type PresetSlugParams = z.infer<typeof PresetSlugParams>;

export const ListPresetsQuery = PaginationQuery.extend({
  q: z.string().trim().toLowerCase().min(1).max(100).optional(),
  ownerId: ProfileId.optional().meta({
    description: "Only presets this profile owns.",
  }),
});
export type ListPresetsQuery = z.infer<typeof ListPresetsQuery>;

export const ListPresetsResponse = z
  .object({
    items: z.array(PresetListing),
    nextCursor: z.string().nullable(),
  })
  .meta({ id: "ListPresetsResponse", description: "One page of scenarios." });
export type ListPresetsResponse = z.infer<typeof ListPresetsResponse>;

export const PresetViewer = z
  .strictObject({
    canSeeEarnings: z.boolean().meta({
      description:
        "Whether the signed-in user may open this preset's earnings: `true` for its team and for staff who can view credits.",
      example: false,
    }),
  })
  .meta({
    id: "PresetViewer",
    description:
      "The signed-in user's relationship to this preset, for deciding which links to render. Absent without a session.",
  });
export type PresetViewer = z.infer<typeof PresetViewer>;

export const GetPresetResponse = z
  .object({
    preset: PresetDetail,
    viewer: PresetViewer.optional(),
    moderation: ItemModeration.optional().meta({
      description:
        "The preset's moderation state, such as a removal. Returned only to its owner, by Get a preset.",
    }),
  })
  .meta({ id: "GetPresetResponse", description: "One preset and its pinned world artifact." });
export type GetPresetResponse = z.infer<typeof GetPresetResponse>;

export const PresetVersionParams = z.strictObject({
  versionId: PresetVersionId,
});
export type PresetVersionParams = z.infer<typeof PresetVersionParams>;

export const PresetVersionFileParams = PresetVersionParams.extend({
  file: CatalogFileName,
});
export type PresetVersionFileParams = z.infer<typeof PresetVersionFileParams>;
